import React from "react";
import { Card, Row, Col, Form, Badge } from "react-bootstrap";
import { Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const PMStatus: React.FC = () => {
    const target = 599;
    const actual = 491;
    const completedPercent = ((actual / target) * 100).toFixed(2);

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
    ];

    const currentMonth = months[new Date().getMonth()];

    const chartData = {
        labels: ["Completed", "Ongoing", "Pending", "Remaining"],
        datasets: [
            {
                data: [491, 6, 13, 102],
                backgroundColor: [
                    "#7CB342", // green
                    "#FBC02D", // yellow
                    "#26C6DA", // teal
                    "#E0E0E0", // grey
                ],
                borderWidth: 0,
            },
        ],
    };

    const chartOptions = {
        cutout: "70%",
        plugins: {
            legend: { display: false },
        },
        maintainAspectRatio: false,
    };

    return (
        <Card className="shadow-sm border-0 rounded-4 p-4">
            {/* Header */}
            <Row className="align-items-center mb-3">
                <Col>
                    <h5 className="fw-bold mb-0">PM Status</h5>
                </Col>
                <Col xs="auto">
                    <Form.Select size="sm" defaultValue={currentMonth}>
                        {months.map((m) => (
                            <option key={m} value={m}>
                                {m}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            {/* KPI Cards */}
            <Row className="g-3 mb-4">
                <Col md={4}>
                    <StatCard title="PM Target Ratio" value={target} />
                </Col>
                <Col md={4}>
                    <StatCard
                        title="PM Ratio Actual"
                        value={actual}
                        valueClass="text-success"
                    />
                </Col>
                <Col md={4}>
                    <StatCard
                        title="Completion %"
                        value={`${completedPercent}%`}
                        valueClass="text-primary"
                    />
                </Col>
            </Row>

            {/* Chart + Status */}
            <Card className="border-0 bg-light rounded-4">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={5} className="position-relative" style={{ height: 260 }}>
                            <Doughnut data={chartData} options={chartOptions} />
                            <div
                                className="position-absolute top-50 start-50 translate-middle fw-bold"
                                style={{ fontSize: 22 }}
                            >
                                {completedPercent}%
                            </div>
                        </Col>

                        <Col md={7}>
                            <StatusRow label="Planned" value={586} bg="primary" />
                            <StatusRow label="Pending (Prev. Month)" value={13} bg="info" />
                            <StatusRow label="Completed" value={491} bg="success" />
                            <StatusRow label="Ongoing" value={6} bg="warning" />
                            <StatusRow
                                label="Remaining (Curr. Month)"
                                value={102}
                                bg="danger"
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </Card>
    );
};

/* ---------------- Sub Components ---------------- */

const StatCard = ({
    title,
    value,
    valueClass = "",
}: {
    title: string;
    value: string | number;
    valueClass?: string;
}) => (
    <Card className="shadow-sm border-0 rounded-3 h-100">
        <Card.Body className="text-center py-3">
            <div className="text-muted small mb-1">{title}</div>
            <div className={`fs-4 fw-bold ${valueClass}`}>{value}</div>
        </Card.Body>
    </Card>
);

const StatusRow = ({
    label,
    value,
    bg,
}: {
    label: string;
    value: number;
    bg: string;
}) => (
    <div className="d-flex justify-content-between align-items-center bg-white rounded px-3 py-2 mb-2 shadow-sm">
        <span className="fw-medium">{label}</span>
        <Badge bg={bg} pill>
            {value}
        </Badge>
    </div>
);

export default PMStatus;
